import { SelectOption } from '../../model/selectOption'

export type TreeSelectOption = {
  value: string
  name: string
  iconUrl?: string
  tooltip?: string
  parentValue: string | undefined
  children: TreeSelectOption[]
}

export const buildTree = (
  options: SelectOption[],
  parentMap: Record<string, string>
): TreeSelectOption[] => {
  const nodeMap = new Map<string, TreeSelectOption>()
  for (const opt of options) {
    nodeMap.set(opt.value, {
      value: opt.value,
      name: opt.name,
      iconUrl: opt.iconUrl,
      tooltip: opt.tooltip,
      parentValue: parentMap[opt.value],
      children: [],
    })
  }

  const roots: TreeSelectOption[] = []
  for (const node of nodeMap.values()) {
    const parent = node.parentValue ? nodeMap.get(node.parentValue) : undefined
    if (parent) {
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  }

  return roots
}

export const countTreeNodes = (roots: TreeSelectOption[]): number => {
  let count = 0
  const countNode = (node: TreeSelectOption) => {
    count++
    node.children.forEach(countNode)
  }
  roots.forEach(countNode)
  return count
}

export const isDescendantOf = (
  nodeValue: string,
  ancestorValue: string,
  parentMap: Record<string, string>
): boolean => {
  let current = parentMap[nodeValue]
  while (current) {
    if (current === ancestorValue) return true
    current = parentMap[current]
  }
  return false
}

export const filterTree = (
  roots: TreeSelectOption[],
  filterText: string
): TreeSelectOption[] => {
  if (!filterText) return roots

  const lower = filterText.toLowerCase()

  const filterNode = (node: TreeSelectOption): TreeSelectOption | undefined => {
    const filteredChildren = node.children
      .map(filterNode)
      .filter((c): c is TreeSelectOption => c !== undefined)

    const selfMatches = node.name.toLowerCase().includes(lower)
    if (selfMatches || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren }
    }
    return undefined
  }

  return roots
    .map(filterNode)
    .filter((n): n is TreeSelectOption => n !== undefined)
}
